<template>
    <div class="searchchooser">
        <b-autocomplete
            :data="results"
            :placeholder="placeholder ? placeholder : $t('search')"
            field="title"
            :loading="isSearching"
            @typing="doAutoComplete"
            icon-pack="fas"
            icon="search"
            class="search"
            clearable
            :open-on-focus="true"
            :keep-first="true"
            @select="onClick"
            v-if="showDropdown"
            @input="onInput"
            @focus="onFocus"
            :ref="`autocomplete_${_uid}`"
            :class="{ 'has-error': v && v.$anyError }">
            <template slot-scope="props">
                <slot name="results" v-bind="props.option"> </slot>
            </template>
            <template #header>
                <div class="head">
                    <template v-if="isSearching">
                        <loader :text="$t('searching')" />
                    </template>
                    <template v-else>
                        <div>
                            <template v-if="useSearch"
                                ><strong>
                                    {{ labelResults ? labelResults : $t('searchResults') }}
                                </strong>
                            </template>
                            <template v-else-if="useSearch && results.length == 0">
                                {{ $t('noresults') }}
                            </template>
                            <template v-else-if="useRecent">
                                {{ labelRecent ? labelRecent : $t('recentlyUsed') }}
                            </template>
                        </div>
                        <template v-if="showAdd">
                            <div class="ml-auto"></div>
                            <a href="javascript:void(0);" class="button is-success is-small" @click.prevent="doAdd">
                                {{ $t('createNew') }}
                            </a>
                        </template>
                    </template>
                </div>
            </template>
            <template #empty
                ><template v-if="!isSearching">{{ $t('noresults') }}</template></template
            >
            <template #footer>
                <template v-if="moreResults > 0 && !isSearching">
                    <strong>
                        {{ $t('moreResults', { count: moreResults }) }}
                    </strong>
                </template>
            </template>
        </b-autocomplete>

        <!-- has value -->
        <template v-if="value && !showNoValueDisabled && showSelectedFiles">
            <!--Multiple value list-->
            <div class="items" v-if="isMultiple && value.length > 0">
                <v-popover
                    offset="15"
                    :disabled="!usePopover"
                    :class="{
                        pointer: usePopover,
                    }">
                    <div
                        v-for="(item, index) in value.slice(0, slicerCount)"
                        :key="index"
                        class="card card-contact"
                        :class="{ 'has-popover': usePopover }">
                        <slot name="selectedItems" v-bind="item"> </slot>
                        <div class="ml-auto"></div>
                        <button
                            v-if="!usePopover && !hideDetail"
                            @click.prevent="showDetailClick(item)"
                            class="button is-detailMenu on-hover">
                            <font-awesome-icon :icon="['fas', 'eye']" />
                        </button>
                        <b-tag type="is-success" v-if="usePopover">+ {{ value.length - 1 }}</b-tag>
                        <button
                            v-if="!iniDisabled"
                            class="button is-danger is-small on-hover"
                            @click="removeFromSelected(index)">
                            <font-awesome-icon :icon="['fas', 'trash-alt']" />
                        </button>
                    </div>
                    <template v-slot:popover>
                        <div v-for="(item, index) in value" :key="index" class="card card-contact">
                            <slot name="selectedItems" v-bind="item"> </slot>
                            <div class="ml-auto"></div>
                            <button
                                v-if="!hideDetail"
                                @click.prevent="showDetailClick(value)"
                                class="button is-detailMenu on-hover">
                                <font-awesome-icon :icon="['fas', 'eye']" />
                            </button>
                            <button
                                v-if="!iniDisabled"
                                class="button is-danger is-small on-hover"
                                @click="removeFromSelected(index)">
                                <font-awesome-icon :icon="['fas', 'trash-alt']" />
                            </button>
                        </div>
                    </template>
                </v-popover>
            </div>
            <!--Single value-->
            <div class="items" v-else-if="value">
                <div class="card card-contact">
                    <!--Single value list-->
                    <slot name="selectedItems" v-bind="value"> </slot>
                    <div class="ml-auto"></div>
                    <button
                        v-if="!hideDetail"
                        @click.prevent="showDetailClick(value)"
                        class="button is-detailMenu on-hover">
                        <font-awesome-icon :icon="['fas', 'eye']" />
                    </button>
                    <button v-if="!iniDisabled" class="button is-danger is-small on-hover" @click="resetValue()">
                        <font-awesome-icon :icon="['fas', 'trash-alt']" />
                    </button>
                </div>
            </div>
        </template>
        <!--No value and disabled-->
        <template v-else-if="showNoValueDisabled">
            <textInput :iniDisabled="true" />
        </template>

        <span v-if="v && v.$anyError" class="help is-danger">
            <slot name="onError" />
        </span>
    </div>
</template>
<script>
import { debounce } from './../mixins/helpers.js';

import VTooltip from 'v-tooltip';

import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faEye, faTrashAlt } from '@fortawesome/free-solid-svg-icons';

library.add(faEye);
library.add(faTrashAlt);

export default {
    name: 'searchChooser',
    components: { VTooltip, FontAwesomeIcon },
    props: {
        value: [Object, Array, Number],
        v: [Object, Function],
        placeholder: { type: String },
        iniDisabled: { type: Boolean, default: false },
        isMultiple: { type: Boolean, default: false },
        condensed: { type: Boolean, default: false },
        dataRecent: { type: Array },
        dataSearch: { type: Array, required: true },
        totalCount: { type: Number },
        isSearching: { type: Boolean, default: false, required: true },
        hideDetail: { type: Boolean, default: false },
        showSelectedFiles: { type: Boolean, default: true },
        labelRecent: { type: String },
        labelResults: { type: String },
        showAdd: { type: Boolean, default: false },
    },
    data() {
        return {
            searchTerm: '',
            useSearch: false,
        };
    },
    computed: {
        useRecent() {
            if (Array.isArray(this.dataRecent) && this.dataRecent.length > 0) {
                return true;
            }
            return false;
        },
        results() {
            if (this.useSearch || !this.useRecent) {
                return this.dataSearch;
            } else if (this.useRecent) {
                return this.dataRecent;
            } else {
                return [];
            }
        },
        moreResults() {
            let diff = 0;

            if (this.useSearch && this.totalCount) {
                diff = this.totalCount - this.dataSearch.length;
            }
            return diff;
        },
        showDropdown() {
            if (this.iniDisabled) {
                return false;
            }
            if (!this.isMultiple && this.value) {
                return false;
            }
            return true;
        },
        usePopover() {
            if (this.condensed) {
                if (Array.isArray(this.value) && this.value.length > 1) {
                    return true;
                }
            }
            return false;
        },
        slicerCount() {
            if (this.condensed) {
                return 1;
            }
            return this.value.length;
        },
        showNoValueDisabled() {
            if (this.iniDisabled) {
                if (this.isMultiple) {
                    return this.value && Array.isArray(this.value) && this.value.length == 0;
                } else {
                    return this.value ? false : true;
                }
            }
            return false;
        },
    },
    watch: {
        value: {
            handler: function (newValue, oldValue) {
                this.handleValue();
            },
            deep: true,
        },
        searchTerm() {
            if (!this.searchTerm) {
                this.clearSearch();
            } else {
                this.doSearch();
            }
        },
    },
    created() {
        this.handleValue();
    },
    methods: {
        onInput(value) {
            //reset if needed
            if (!value) {
                this.resetValue();
            }
        },
        onFocus(event) {
            this.ensureDropDownVisibility();
            this.$emit('onFocus', this.searchTerm);
        },
        removeDropDownVisibility() {
            /*
      var element = this.$refs[`autocomplete_${this._uid}`].$el;
      var dropdown = element.querySelector(".dropdown-menu");
      dropdown.style.position = null;
      */
        },
        ensureDropDownVisibility() {
            /*
      var element = this.$refs[`autocomplete_${this._uid}`].$el;
      //var modal = element.parentNode.closest(".modal-card-body");
      var dropdown = element.querySelector(".dropdown-menu");
      //console.log(modal.offsetHeight + " vs " + dropdown.offsetHeight);
      dropdown.style.position = "relative";
      */
        },
        doAdd() {
            this.removeDropDownVisibility();
            this.$emit('onAdd', this.searchTerm);
        },
        handleValue() {
            if (!this.dataRecent) {
                this.useSearch = true;
            }
        },
        fetchData() {
            this.$emit('search', this.searchTerm);
        },
        removeFromSelected(index) {
            var value = JSON.parse(JSON.stringify(this.value));
            value.splice(index, 1);
            this.$emit('input', value);
        },
        onClick(item) {
            this.removeDropDownVisibility();
            if (this.value) {
                var value = JSON.parse(JSON.stringify(this.value));
            }
            if (this.isMultiple) {
                if (!this.value) {
                    //if not set, set as array
                    value = [];
                }
                value.push(item);
            } else {
                value = item;
            }
            this.$emit('input', value);
        },
        doSearch() {
            //if (this.searchTerm) {
            this.useSearch = true;
            this.fetchData();
            //}
        },
        clearSearch() {
            this.useSearch = false;
            this.searchTerm = '';
        },
        resetValue() {
            this.clearSearch();
            this.$emit('input', null);
        },
        showDetailClick(item) {
            this.$emit('showDetail', item);
        },
        doAutoComplete: debounce(function (value) {
            this.searchTerm = value;
            this.doSearch();
        }, 500),
    },
    i18n: {
        messages: {
            en: {
                search: 'Search',
                searchResults: 'Search Results',
                noresults: 'No results',
                recentlyUsed: 'Recently used',
                createNew: 'Create new',
                searching: 'Searching',
                moreResults: 'There are {count} more results...',
            },
            fr: {
                search: 'Recherche',
                searchResults: 'Résultats de la recherche',
                noresults: 'Aucun résultat',
                recentlyUsed: 'Récemment utilisé',
                createNew: 'Créer un nouveau',
                searching: 'Rechercher',
                moreResults: 'Il y a {count} autres résultats ...',
            },
            nl: {
                search: 'Search',
                searchResults: 'Zoekresultaten',
                noresults: 'Geen resultaten',
                recentlyUsed: 'Recent gebruikt',
                createNew: 'Nieuw aanmaken',
                searching: 'Zoeken',
                moreResults: 'Er zijn nog {count} resultaten ...',
            },
        },
    },
};
</script>
